import { useEffect, useState } from "react";
import { useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { Card } from "../../components/ui/Card";

interface RefCode {
  code: string;
  displayName: string;
}

interface ResinType {
  resinTypeCode: string;
  displayName: string;
}

interface ResinFamily {
  resinFamilyCode: string;
  displayName: string;
  types: ResinType[];
}

interface Supplier {
  supplierId: number;
  supplierName: string;
  isProducer: boolean;
}

interface SpotNomination {
  spotNominationId: number;
  quotedAtLocal: string;
  supplierId: number | null;
  supplierName: string;
  sourceChannelCode: string | null;
  resinFamilyCode: string;
  resinTypeCode: string | null;
  materialConditionCode: string | null;
  priceCentsPerLb: number;
  currencyCode: string;
  priceBasisCode: string | null;
  packageCode: string | null;
  regionCode: string | null;
  volumeLb: number | null;
  firmnessCode: string;
  notes: string | null;
  status: "Active" | "Voided";
  enteredByAppUserId: number;
  enteredByDisplayName: string;
}

export function SpotNominationsTab() {
  const apiFetch = useApiFetch();
  const { appRole, appUserId } = useAppUser();
  const canManage = (enteredByAppUserId: number) =>
    appRole === "Admin" || (appRole === "Uploader" && enteredByAppUserId === appUserId);
  const { confirm, dialog } = useConfirmDialog();

  const [rows, setRows] = useState<SpotNomination[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Same reference-data endpoints SpotLane's entry form uses, so the edit
  // dialog offers the identical dropdowns rather than free text -- editing
  // a quote should not be able to introduce a code the entry form itself
  // would never let through.
  const [firmness, setFirmness] = useState<RefCode[]>([]);
  const [sourceChannels, setSourceChannels] = useState<RefCode[]>([]);
  const [conditions, setConditions] = useState<RefCode[]>([]);
  const [priceBases, setPriceBases] = useState<RefCode[]>([]);
  const [packages, setPackages] = useState<RefCode[]>([]);
  const [regions, setRegions] = useState<RefCode[]>([]);
  const [families, setFamilies] = useState<ResinFamily[]>([]);
  const [producers, setProducers] = useState<Supplier[]>([]);

  const [editingRow, setEditingRow] = useState<SpotNomination | null>(null);
  const [editSupplierId, setEditSupplierId] = useState("");
  const [editSupplierOther, setEditSupplierOther] = useState("");
  const [editSource, setEditSource] = useState("");
  const [editFamily, setEditFamily] = useState("");
  const [editType, setEditType] = useState("");
  const [editCond, setEditCond] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editBasis, setEditBasis] = useState("");
  const [editPack, setEditPack] = useState("");
  const [editRegion, setEditRegion] = useState("");
  const [editQty, setEditQty] = useState("");
  const [editFirm, setEditFirm] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (dateFrom) params.set("from", new Date(dateFrom).toISOString());
      if (dateTo) params.set("to", new Date(dateTo).toISOString());
      const res = await apiFetch(`spot?${params.toString()}`);
      setRows(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [firmRes, srcRes, condRes, basisRes, packRes, regionRes, taxRes, supRes] = await Promise.all([
          apiFetch("reference/Firmness"),
          apiFetch("reference/SourceChannel"),
          apiFetch("reference/MaterialCondition"),
          apiFetch("reference/PriceBasis"),
          apiFetch("reference/Package"),
          apiFetch("reference/Region"),
          apiFetch("reference/resin-taxonomy"),
          apiFetch("suppliers?producersOnly=true&limit=100"),
        ]);
        if (cancelled) return;
        const [firmData, srcData, condData, basisData, packData, regionData, taxData, supData] = await Promise.all([
          firmRes.json(), srcRes.json(), condRes.json(), basisRes.json(), packRes.json(), regionRes.json(), taxRes.json(), supRes.json(),
        ]);
        setFirmness(firmData); setSourceChannels(srcData); setConditions(condData);
        setPriceBases(basisData); setPackages(packData); setRegions(regionData);
        setFamilies(taxData); setProducers(supData);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (row: SpotNomination) => {
    setEditingRow(row);
    setEditSupplierId(row.supplierId ? String(row.supplierId) : "__other");
    setEditSupplierOther(row.supplierId ? "" : row.supplierName);
    setEditSource(row.sourceChannelCode ?? "");
    setEditFamily(row.resinFamilyCode);
    setEditType(row.resinTypeCode ?? "");
    setEditCond(row.materialConditionCode ?? "");
    setEditPrice(String(row.priceCentsPerLb));
    setEditBasis(row.priceBasisCode ?? "");
    setEditPack(row.packageCode ?? "");
    setEditRegion(row.regionCode ?? "");
    setEditQty(row.volumeLb != null ? String(row.volumeLb) : "");
    setEditFirm(row.firmnessCode);
    setEditNotes(row.notes ?? "");
  };

  const editSelectedFamily = families.find((f) => f.resinFamilyCode === editFamily);
  const editSupplierName = editSupplierId === "__other" ? editSupplierOther.trim() : producers.find((p) => String(p.supplierId) === editSupplierId)?.supplierName ?? "";
  const editReady = !!editSupplierName && !!editFamily && !!editPrice && !!editFirm;

  const saveEdit = async () => {
    if (!editingRow || !editReady || editSaving) return;
    setEditSaving(true);
    try {
      const newSupplierId = editSupplierId === "__other" ? null : Number(editSupplierId);
      const supplierChanged = newSupplierId !== editingRow.supplierId
        || (newSupplierId === null && editSupplierOther.trim() !== editingRow.supplierName);
      await apiFetch(`spot/${editingRow.spotNominationId}`, {
        method: "PATCH",
        body: JSON.stringify({
          // supplierId/supplierNameOther clear each other server-side, so
          // only sent when actually changed -- otherwise every edit would
          // silently re-stamp (or unlink) the supplier.
          ...(supplierChanged
            ? newSupplierId === null
              ? { supplierId: null, supplierNameOther: editSupplierOther.trim() }
              : { supplierId: newSupplierId }
            : {}),
          sourceChannelCode: editSource || null,
          resinFamilyCode: editFamily,
          resinTypeCode: editType || null,
          materialConditionCode: editCond || null,
          priceCentsPerLb: parseFloat(editPrice),
          priceBasisCode: editBasis || null,
          packageCode: editPack || null,
          regionCode: editRegion || null,
          volumeLb: editQty ? Number(editQty) : null,
          firmnessCode: editFirm,
          notes: editNotes.trim() || null,
        }),
      });
      setEditingRow(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditSaving(false);
    }
  };

  const void_ = async (row: SpotNomination) => {
    const reason = await confirm({
      title: "Void spot quote",
      message: `Void this ${row.supplierName} spot quote?`,
      reasonLabel: "Reason",
    });
    if (reason === null) return;
    try {
      await apiFetch(`spot/${row.spotNominationId}/void`, {
        method: "POST",
        body: JSON.stringify({ reason: reason || "Voided from Resin8 Data View" }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const selectable = rows.filter((r) => r.status === "Active" && canManage(r.enteredByAppUserId));
  const allSelected = selectable.length > 0 && selectable.every((r) => selectedIds.has(r.spotNominationId));

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      selectable.forEach((r) => next.add(r.spotNominationId));
      return next;
    });
  };

  const bulkVoid = async () => {
    const selectedRows = rows.filter((r) => selectedIds.has(r.spotNominationId));
    if (!selectedRows.length) return;

    const reason = await confirm({
      title: "Void selected spot quotes",
      message: `Void ${selectedRows.length} spot quote(s)?`,
      reasonLabel: "Reason",
    });
    if (reason === null) return;

    const confirmAgain = await confirm({
      title: "Are you sure?",
      message: `This cannot be undone. Confirm voiding ${selectedRows.length} spot quote(s)?`,
      confirmLabel: "Yes, void",
    });
    if (confirmAgain === null) return;

    try {
      await Promise.all(
        selectedRows.map((r) =>
          apiFetch(`spot/${r.spotNominationId}/void`, {
            method: "POST",
            body: JSON.stringify({ reason: reason || "Bulk voided from Resin8 Data View" }),
          }),
        ),
      );
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
    <section className="lane on">
      <h2>Spot Nominations</h2>
      <p className="lede">Every spot quote logged, with the ability to void an entry that was recorded in error.</p>

      <div className="sec">
        <div className="card">
          <div className="inline">
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--grey)" }}>
              Quoted from
              <input className="ctl" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--grey)" }}>
              Quoted to
              <input className="ctl" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
      )}

      {selectedIds.size > 0 && (
        <div className="sec">
          <div className="card" style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{selectedIds.size} selected</span>
            <button className="btn danger" onClick={bulkVoid}>
              <span className="material-symbols-sharp">block</span>Void Selected
            </button>
          </div>
        </div>
      )}

      <div className="sec">
        <div className="card" style={{ padding: 0, overflow: "auto", maxWidth: "100%" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 1 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectable.length === 0} />
                </th>
                <th>Quoted</th>
                <th>Supplier</th>
                <th>Resin</th>
                <th>Price (¢/lb)</th>
                <th>Region</th>
                <th>Firmness</th>
                <th>Entered by</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={10}>No spot quotes match this range.</td></tr>}
              {rows.map((r) => (
                <tr key={r.spotNominationId}>
                  <td>
                    {r.status === "Active" && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.spotNominationId)}
                        disabled={!canManage(r.enteredByAppUserId)}
                        onChange={() => toggleOne(r.spotNominationId)}
                      />
                    )}
                  </td>
                  <td>{new Date(r.quotedAtLocal).toLocaleString()}</td>
                  <td>{r.supplierName}</td>
                  <td>{r.resinFamilyCode}{r.resinTypeCode ? ` / ${r.resinTypeCode}` : ""}</td>
                  <td>{r.priceCentsPerLb.toFixed(2)} {r.currencyCode}</td>
                  <td>{r.regionCode ?? "—"}</td>
                  <td>{r.firmnessCode}</td>
                  <td>{r.enteredByDisplayName}</td>
                  <td><span className={`chip ${r.status === "Active" ? "ok" : "bad"}`}>{r.status}</span></td>
                  <td style={{ display: "flex", gap: 6 }}>
                    {r.status === "Active" && (
                      <>
                        <button
                          className="btn ghost"
                          disabled={!canManage(r.enteredByAppUserId)}
                          title={!canManage(r.enteredByAppUserId) ? "You can only edit quotes you entered, unless you're an Admin" : undefined}
                          onClick={() => openEdit(r)}
                        >
                          <span className="material-symbols-sharp">edit</span>
                        </button>
                        <button
                          className="btn ghost"
                          disabled={!canManage(r.enteredByAppUserId)}
                          title={!canManage(r.enteredByAppUserId) ? "You can only void quotes you entered, unless you're an Admin" : undefined}
                          onClick={() => void_(r)}
                        >
                          <span className="material-symbols-sharp">block</span>Void
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {editingRow && (
      <div className="confirm-overlay" role="presentation" onClick={() => setEditingRow(null)}>
        <Card className="w-full max-w-5xl" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
          <h3>Edit Spot Quote</h3>
          <p style={{ marginTop: -6 }}>Quoted {new Date(editingRow.quotedAtLocal).toLocaleString()} by {editingRow.enteredByDisplayName}</p>

          <div className="form" style={{ marginBottom: 18 }}>
            <div className="f4">
              <label className="fl">Supplier<span className="req">*</span></label>
              <select className="ctl" value={editSupplierId} onChange={(e) => setEditSupplierId(e.target.value)}>
                <option value="">Select a supplier</option>
                {producers.map((p) => <option key={p.supplierId} value={p.supplierId}>{p.supplierName}</option>)}
                <option value="__other">Someone else, type the name</option>
              </select>
              {editSupplierId === "__other" && (
                <input
                  type="text" className="ctl" placeholder="Supplier name" style={{ marginTop: 10 }}
                  value={editSupplierOther} onChange={(e) => setEditSupplierOther(e.target.value)}
                />
              )}
            </div>

            <div className="f4">
              <label className="fl">How it came in</label>
              <select className="ctl" value={editSource} onChange={(e) => setEditSource(e.target.value)}>
                {sourceChannels.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f4">
              <label className="fl">Resin family<span className="req">*</span></label>
              <select className="ctl" value={editFamily} onChange={(e) => { setEditFamily(e.target.value); setEditType(""); }}>
                <option value="">Select a family</option>
                {families.map((f) => <option key={f.resinFamilyCode} value={f.resinFamilyCode}>{f.displayName}</option>)}
              </select>
            </div>

            <div className="f4">
              <label className="fl">Resin type or grade</label>
              <select className="ctl" value={editType} disabled={!editFamily} onChange={(e) => setEditType(e.target.value)}>
                <option value="">{editFamily ? "All grades in the family" : "Pick a family first"}</option>
                {editSelectedFamily?.types.map((t) => <option key={t.resinTypeCode} value={t.resinTypeCode}>{t.displayName}</option>)}
              </select>
            </div>

            <div className="f4">
              <label className="fl">Material condition</label>
              <select className="ctl" value={editCond} onChange={(e) => setEditCond(e.target.value)}>
                {conditions.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f12">
              <label className="fl">Firmness</label>
              <div className="seg" role="group" aria-label="Firmness">
                {firmness.map((f) => (
                  <button key={f.code} type="button" className="whitespace-nowrap" aria-pressed={editFirm === f.code} onClick={() => setEditFirm(f.code)}>{f.displayName}</button>
                ))}
              </div>
            </div>

            <div className="f3">
              <label className="fl">Quoted level<span className="req">*</span></label>
              <div className="inline joined">
                <input type="number" className="ctl" step={0.25} min={0} placeholder="0.00" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} />
                <span className="suffix">¢/lb</span>
              </div>
            </div>

            <div className="f3">
              <label className="fl">Price basis</label>
              <select className="ctl" value={editBasis} onChange={(e) => setEditBasis(e.target.value)}>
                {priceBases.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f3">
              <label className="fl">Package</label>
              <select className="ctl" value={editPack} onChange={(e) => setEditPack(e.target.value)}>
                {packages.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f3">
              <label className="fl">Region</label>
              <select className="ctl" value={editRegion} onChange={(e) => setEditRegion(e.target.value)}>
                {regions.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f5">
              <label className="fl">Volume offered</label>
              <div className="inline joined">
                <input type="number" className="ctl" step={1000} min={0} placeholder="e.g. 180000" value={editQty} onChange={(e) => setEditQty(e.target.value)} />
                <span className="suffix">lb</span>
              </div>
            </div>

            <div className="f12">
              <label className="fl">Context</label>
              <textarea className="ctl" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
            </div>
          </div>

          <div className="confirm-actions">
            <button className="btn ghost" onClick={() => setEditingRow(null)}>Cancel</button>
            <button className="btn" disabled={!editReady || editSaving} onClick={saveEdit}>
              {editSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </Card>
      </div>
    )}

    {dialog}
    </>
  );
}
